import clsx from 'clsx'
import dayjs from 'dayjs'
import { scopedTranslation } from '@frontend/utils/I18n'
import { whenRange } from '@utils/callbackTime'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import Badge from '@components/atoms/Badge'
import { type IconTypes } from '@components/utils/Icon'

const t = scopedTranslation('components.supporter_details')

const colorByStatus = {
  available: 'blue',
  completed: 'success',
  skipped: 'neutral',
  skipped_by_leader: 'warning',
  skipped_callback: 'warning',
  unavailable: 'neutral',
  not_callable: 'error',
} as const

export type StatusType = keyof typeof colorByStatus

export type ActiveSkip = {
  skipType: string
  reason: string | null
  expiresAt: string | null
} | null

type SupporterStatusBadgeProps = {
  status: StatusType
  unavailableReason: string | null
  unavailableUntil: string | null
  activeSkip?: ActiveSkip
  callbackDueAt?: string | null
  callbackFlexible?: boolean
}

const unavailableReason = (reason: string, until: string | null) => {
  const getDescription = (reason: string): string => {
    switch (reason) {
      case 'recently_called':
        return t('unavailable_status.recently_called_description')
      case 'call_list_time_restriction':
        return t('unavailable_status.call_list_time_restriction_description')
      default:
        return t('unavailable_status.other_description')
    }
  }

  const description = getDescription(reason)
  const untilText = formatUnavailableUntilTime(until)

  return (
    <div className="tw:space-y-1">
      <div>{description}</div>
      {untilText && <div className="tw:text-xs tw:opacity-90">{untilText}</div>}
    </div>
  )
}

const completedDetails = () => {
  return <div>{t('call_status.completed_description')}</div>
}

const callbackHoldDetails = (dueAt: string | null | undefined) => {
  if (!dueAt) return null
  return <div>{t('call_status.skipped_callback_description', { when: dayjs(dueAt).format('ddd D MMM, h:mm A') })}</div>
}

const skippedReason = () => {
  return <div>{t('call_status.skipped_description')}</div>
}

const leaderSkippedDetails = (activeSkip: ActiveSkip) => {
  if (!activeSkip) return null

  const timeText = activeSkip.expiresAt ? formatUnavailableUntilTime(activeSkip.expiresAt) : null
  const skipTypeLabels: Record<string, string> = {
    permanent: t('call_status.skipped_by_leader_permanent'),
    today: t('call_status.skipped_by_leader_today'),
    this_cycle: t('call_status.skipped_by_leader_cycle'),
  }
  const skipTypeText = skipTypeLabels[activeSkip.skipType] ?? null

  return (
    <div className="tw:space-y-1">
      {skipTypeText && <div>{skipTypeText}</div>}
      {timeText && !skipTypeText && <div>{timeText}</div>}
      {activeSkip.reason && (
        <div className="tw:text-xs tw:opacity-90">{t('call_status.skipped_reason', { reason: activeSkip.reason })}</div>
      )}
    </div>
  )
}

const formatUnavailableUntilTime = (until: string | null) => {
  if (!until) return ''

  try {
    const untilDate = dayjs(until)
    if (!untilDate.isValid()) return ''

    const now = dayjs()

    const diffHours = untilDate.diff(now, 'hours')
    const diffMinutes = untilDate.diff(now, 'minutes')

    if (diffHours >= 24) {
      return t('unavailable_status.until_date', { date: untilDate.format('MMM D') })
    } else if (diffHours >= 1) {
      return t('unavailable_status.until_hours', { hours: diffHours })
    } else {
      return t('unavailable_status.until_minutes', { minutes: diffMinutes })
    }
  } catch {
    return ''
  }
}

type StatusConfig = {
  status: StatusType
  text: string
  testId: string
  icon?: IconTypes
}

const getStatusConfig = (status: StatusType, callbackWhen: string): StatusConfig => {
  switch (status) {
    case 'available':
      return {
        status,
        text: t('call_status.available'),
        testId: 'available-status-badge',
      }
    case 'completed':
      return {
        status,
        text: t('call_status.completed'),
        testId: 'completed-status-badge',
        icon: 'info-circle',
      }
    case 'skipped':
      return {
        status,
        text: t('call_status.skipped'),
        testId: 'skipped-status-badge',
        icon: 'info-circle',
      }
    case 'skipped_by_leader':
      return {
        status,
        text: t('call_status.skipped_by_leader'),
        testId: 'skipped-by-leader-status-badge',
        icon: 'info-circle',
      }
    case 'skipped_callback':
      return {
        status,
        text: t('call_status.skipped_callback', { when: callbackWhen }),
        testId: 'skipped-callback-status-badge',
        icon: 'calendar',
      }
    case 'unavailable':
      return {
        status,
        text: t('call_status.unavailable'),
        testId: 'unavailable-status-badge',
        icon: 'info-circle',
      }
    case 'not_callable':
      return {
        status,
        text: t('call_status.not_callable'),
        testId: 'not-callable-status-badge',
      }
    default:
      throw new Error(`Unknown status: ${status as string}`)
  }
}

const renderTooltipContent = (
  status: StatusType,
  reason: string | null,
  until: string | null,
  activeSkip: ActiveSkip = null,
  callbackDueAt: string | null = null
) => {
  if (status === 'skipped_by_leader') return leaderSkippedDetails(activeSkip)
  if (status === 'skipped_callback') return callbackHoldDetails(callbackDueAt)
  if (status === 'unavailable' && reason) return unavailableReason(reason, until)
  if (status === 'skipped') return skippedReason()
  if (status === 'completed') return completedDetails()

  return null
}

export function SupporterStatusBadge({
  status,
  unavailableReason,
  unavailableUntil,
  activeSkip,
  callbackDueAt,
  callbackFlexible = false,
}: SupporterStatusBadgeProps) {
  const statusConfig = getStatusConfig(status, callbackDueAt ? whenRange(callbackDueAt, new Date(), callbackFlexible) : '')
  const tooltipContent = renderTooltipContent(status, unavailableReason, unavailableUntil, activeSkip, callbackDueAt)

  const badge = (
    <Badge
      shape="rounded"
      bordered
      color={colorByStatus[statusConfig.status]}
      trailingIcon={statusConfig.icon}
      data-testid={statusConfig.testId}
      className={clsx('tw:whitespace-nowrap', tooltipContent && 'tw:cursor-pointer')}
    >
      {statusConfig.text}
    </Badge>
  )

  if (!tooltipContent) return badge

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
